import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMoleculeRegistration,
  publishMolecule,
  unpublishMolecule,
  type MoleculeRegistration
} from '@peruselab/api-client/molecules';
import { Icon } from '../../../components/Icon';
import { MoleculeCredit } from './MoleculeCredit';
import { MoleculeVisibilityDialog } from './MoleculeVisibilityDialog';
import { announceMoleculeVisibility } from './moleculeVisibilitySync';

/**
 * #1060 (ADR 0019 D3, D13): on a Monograph, says when the molecule is private -- only the reader's
 * own space holds it -- and offers Make public when that registration is theirs to publish (an
 * administrator of the space) -- and Make private again to undo it (owner, 2026-10-23). A line of
 * its own under the header (whose controls never wrap, owner 2026-10-12); a public molecule that
 * isn't the reader's to change shows nothing.
 */
export function MoleculeVisibility({ moleculeId, name }: { readonly moleculeId: string; readonly name: string }) {
  const { t } = useTranslation();
  const [registration, setRegistration] = useState<MoleculeRegistration | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ readonly kind: 'status' | 'alert'; readonly text: string } | null>(null);
  const [confirming, setConfirming] = useState<'publish' | 'unpublish' | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMoleculeRegistration(moleculeId)
      .then((loaded) => {
        if (!cancelled) setRegistration(loaded);
      })
      .catch(() => {
        // The page itself reports a molecule it can't open; nothing to add here.
      });
    return () => {
      cancelled = true;
    };
  }, [moleculeId]);

  const change = async (action: 'publish' | 'unpublish') => {
    setConfirming(null);
    setBusy(true);
    setMessage(null);
    try {
      if (action === 'publish') await publishMolecule(moleculeId);
      else await unpublishMolecule(moleculeId);
      const now = await getMoleculeRegistration(moleculeId);
      setRegistration(now);
      announceMoleculeVisibility(moleculeId, now.visibility);
      setMessage({
        kind: 'status',
        text:
          action === 'publish'
            ? t('moleculeVisibility.nowPublic')
            : now.visibility === 'public'
              ? t('moleculeVisibility.stillPublic')
              : t('moleculeVisibility.nowPrivate')
      });
    } catch (error) {
      setMessage({
        kind: 'alert',
        text: error instanceof Error ? error.message : t('moleculeVisibility.changeFailed')
      });
    } finally {
      setBusy(false);
    }
  };

  if (!registration) return null;
  const credited = (registration.contributions?.length ?? 0) > 0 || Boolean(registration.externalRecord);
  if (
    registration.visibility === 'public' &&
    registration.yours !== 'public' &&
    !registration.provisional &&
    !credited &&
    !message
  ) {
    return null;
  }
  return (
    <div className="molecule-visibility">
      {registration.visibility === 'private' ? (
        // Said in words, not a tooltip: a phone has no hover.
        <p className="molecule-private-badge" data-testid="molecule-private">
          <Icon name="lock" size="1rem" />
          <strong>{t('moleculeVisibility.privateBadge')}</strong>
          <span>{t('moleculeVisibility.privateHint')}</span>
        </p>
      ) : null}
      {/* Yours, and public: said in words too, so it isn't mistaken for the drawing's own setting. */}
      {registration.visibility === 'public' && registration.yours === 'public' ? (
        <p className="molecule-private-badge" data-testid="molecule-public">
          <Icon name="public" size="1rem" />
          <strong>{t('moleculeVisibility.publicBadge')}</strong>
          <span>{t('moleculeVisibility.publicHint')}</span>
        </p>
      ) : null}
      <MoleculeCredit registration={registration} />
      {/* #1064: drawn, in nothing saved yet. */}
      {registration.provisional ? (
        <p className="molecule-private-badge" data-testid="molecule-provisional">
          <Icon name="draw" size="1rem" />
          <strong>{t('drawnMolecules.provisional')}</strong>
          <span>{t('drawnMolecules.provisionalHint')}</span>
        </p>
      ) : null}
      {/* Your space's own private registration, but publishing is an administrator's (#1051). */}
      {registration.yours === 'private' && !registration.canPublish ? (
        <span className="molecule-visibility-message" data-testid="molecule-publish-by-admin">
          {t('moleculeVisibility.askAdministrator')}
        </span>
      ) : null}
      {registration.canPublish ? (
        <button
          type="button"
          className="icon-button"
          data-testid="molecule-make-public"
          aria-label={t('moleculeVisibility.makePublic')}
          title={t('moleculeVisibility.makePublicHint')}
          disabled={busy}
          onClick={() => setConfirming('publish')}
        >
          <Icon name="public" />
        </button>
      ) : null}
      {registration.canUnpublish ? (
        <button
          type="button"
          className="icon-button"
          data-testid="molecule-make-private"
          aria-label={t('moleculeVisibility.makePrivate')}
          title={t('moleculeVisibility.makePrivate')}
          disabled={busy}
          onClick={() => setConfirming('unpublish')}
        >
          <Icon name="lock" />
        </button>
      ) : null}
      {confirming ? (
        <MoleculeVisibilityDialog
          action={confirming}
          name={name}
          onConfirm={() => void change(confirming)}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
      {message ? (
        <span className="molecule-visibility-message" role={message.kind}>
          {message.text}
        </span>
      ) : null}
    </div>
  );
}
